import { AlignerMonitoringCell, SolidStatusPill, Tooltip } from '@odo/shared-ui';
import { type WorkQueueCase, type StatusMeta, type OutgoingAction } from '../../services/workQueue';
import { routeForAction } from '../../services/workflowActionRoutes';
import { findOdoForwardAction } from './odoForwardAction';
import { Avatar } from '../Avatar';
import AssigneeSelector from '../conversations/AssigneeSelector';
import { PatientPracticeCell } from '../shared/PatientPracticeCell';
import { computeCaseTags } from './caseTags';
import { CaseTagList } from './CaseTagPills';
import { useCaseAssignment } from '../../hooks/useCaseAssignment';
import { PriorityPill } from './priority/PriorityPill';
import { vaStatusCounts } from './vaStatusCounts';
import type { OpenVirtualAppointmentStatus } from '../virtual-appointments/openVirtualAppointments';
import { VIRTUAL_APPOINTMENT_STATUS_LABELS } from '@odo/shared-ui/clinical/virtualAppointments/summary';
import { virtualAppointmentStatusBg } from '../virtual-appointments/virtualAppointmentStatusColors';

export function PatientCell({ workCase: c }: { workCase: WorkQueueCase }) {
    return (
        <PatientPracticeCell
            patientName={c.name}
            nameClassName="text-xs font-normal leading-4 tracking-[0.005em] text-gray-800 underline cursor-pointer truncate"
            avatar={
                <Avatar
                    user={{ name: c.name, email: '' }}
                    imageURL={c.profilePictureUrl ?? undefined}
                    thumbnailURL={c.profileThumbnailUrl ?? undefined}
                />
            }
            badges={
                // Priority badge is display-only; editing lives in the detail
                // panel. Tags cap at 4 then collapse to "+N".
                <div className="flex items-center gap-1 flex-shrink-0">
                    <PriorityPill priority={c.priority} />
                    <CaseTagList tags={computeCaseTags(c)} />
                </div>
            }
            practiceName={c.practice}
            doctorName={c.practiceDoctor}
            format="doctor-practice"
            sublineToneClassName="text-gray-500"
        />
    );
}

export function AssigneeCell({ workCase: c }: { workCase: WorkQueueCase }) {
    const { roles, currentUserEmail, handleAssign, handleUnassign } = useCaseAssignment(
        c,
        'AssigneeCell'
    );
    return (
        <AssigneeSelector
            assignedTo={c.assignee}
            roles={roles}
            onAssign={handleAssign}
            onUnassign={handleUnassign}
            currentUserEmail={currentUserEmail}
        />
    );
}

export function DmCell({ workCase: c }: { workCase: WorkQueueCase }) {
    return (
        <AlignerMonitoringCell
            current={c.currentAligner}
            total={c.totalAligners}
            monitoringStatus={c.monitoringStatus}
        />
    );
}

// Light tints of the VA status chip colours, the way the notification level pills tint theirs.
const VA_COUNT_TONES: Record<OpenVirtualAppointmentStatus, string> = {
    open: 'bg-green-50 text-green-700',
    waiting_patient: 'bg-pink-50 text-pink-700',
    waiting_doctor: 'bg-amber-50 text-amber-700'
};

export function VaCountsCell({ workCase: c }: { workCase: WorkQueueCase }) {
    const counts = vaStatusCounts(c.vaCounts);
    const pills = counts.filter(({ count }) => count > 0);
    if (pills.length === 0) return <span className="text-xs text-gray-400">-</span>;
    const breakdown = counts
        .map(({ status, count }) => `${VIRTUAL_APPOINTMENT_STATUS_LABELS[status]}: ${count}`)
        .join('\n');
    // The group tooltip carries the full breakdown; an empty pill tooltip avoids a second bubble.
    return (
        <Tooltip content={breakdown} side="top" focusable>
            <span className="flex min-w-0 items-center gap-1.5 overflow-hidden">
                {pills.map(({ status, count }) => (
                    <SolidStatusPill
                        key={status}
                        value={status}
                        label={`${count} ${VIRTUAL_APPOINTMENT_STATUS_LABELS[status]}`}
                        toneClassName={VA_COUNT_TONES[status]}
                        dotClassName={virtualAppointmentStatusBg(status)}
                        className="min-w-0"
                        tooltip=""
                    />
                ))}
            </span>
        </Tooltip>
    );
}

function primaryForwardAction(statusMeta: StatusMeta | undefined): OutgoingAction | null {
    if (!statusMeta) return null;
    return findOdoForwardAction(statusMeta.outgoingActions) ?? null;
}

interface ActionCellProps {
    workCase: WorkQueueCase;
    statusMeta: StatusMeta | undefined;
    onAction: (c: WorkQueueCase, outgoingAction: OutgoingAction) => void;
    onStartTask: (id: string) => void;
}

export function ActionCell({ workCase: c, statusMeta, onAction, onStartTask }: ActionCellProps) {
    const primaryAction = primaryForwardAction(statusMeta);
    const task = statusMeta?.task ?? null;
    const directActionWired =
        primaryAction !== null && routeForAction(primaryAction.action) !== null;
    const buttonEnabled = task !== null || directActionWired;
    const buttonLabel = task?.label ?? primaryAction?.label ?? null;

    if (!buttonLabel) return <span className="text-xs text-gray-400 italic">-</span>;

    const handleClick = () => {
        if (task) {
            onStartTask(c.id);
        } else if (primaryAction && directActionWired) {
            onAction(c, primaryAction);
        }
    };

    return (
        <Tooltip
            content={buttonEnabled ? buttonLabel : 'Action route not implemented yet'}
            side="top"
        >
            <span className="inline-block max-w-full">
                <button
                    onClick={handleClick}
                    disabled={!buttonEnabled}
                    className="inline-flex items-center max-w-full px-3 py-1.5 rounded-md border border-[rgb(55,115,168)] text-[rgb(55,115,168)] text-xs font-bold bg-white hover:bg-[rgb(55,115,168)] hover:text-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-white disabled:hover:text-[rgb(55,115,168)]"
                >
                    <span className="truncate">{buttonLabel}</span>
                </button>
            </span>
        </Tooltip>
    );
}
